import {
  Button,
  MenuItem,
  FormControl as MuiFormControl,
  Select,
  styled,
  type SelectChangeEvent,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTransferDevice } from '~/api/devices';
import { useAppContext } from '~/contexts';
import { assert } from '~/lib/assert';
import { Dialog, DialogContentTitle, DialogTitle } from '../dialogs/components';
import { DialogActions, DialogContent } from '../dialogs/lib/styles';
import type { TransferDevice__Device as Device } from './TransferDialog.generated';

/* GraphQL */ `#graphql
fragment TransferDevice__Device on Device {
  id
  network {
    id
  }
}
`;

const FormControl = styled(MuiFormControl)(({ theme }) => ({
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

export const useTransferDeviceDialog = () => {
  const [open, setOpen] = useState(false);
  const [device, setDevice] = useState<Device>();

  const props = useMemo(() => ({ device, open, setOpen }), [device, open]);

  const openDialog = useCallback((device: Device) => {
    setDevice(device);
    setOpen(true);
  }, []);

  return [openDialog, props] as const;
};

export interface TransferDialogProps {
  device: Device | undefined;
  open: boolean;
  setOpen: (value: boolean) => void;
}

export const TransferDialog = ({ device, open, setOpen }: TransferDialogProps) => {
  // hooks
  const { t } = useTranslation();
  const { currentUser, currentNetwork } = useAppContext();

  const [networkId, setNetworkId] = useState<number>(currentNetwork.id);

  const [transferDevice] = useTransferDevice();
  // end hooks

  if (device == null) return null;

  const transfer = async () => {
    setOpen(false);
    await transferDevice({ variables: { deviceId: device.id, networkId } });
  };

  const handleNetwork = (event: SelectChangeEvent<number>) => {
    const value = event.target.value;
    setNetworkId(value as number);
    const selectNetwork = currentUser.networks.find((network) => network.id === value);
    assert(selectNetwork !== undefined, 'select Network can not be undefined.');
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={() => setOpen(false)}
      TransitionProps={{
        onExited: () => {
          setNetworkId(currentNetwork.id);
        },
      }}
      open={open}
    >
      <DialogTitle onClose={() => setOpen(false)}>
        {t('components.transfer_dialog.content.title')}
      </DialogTitle>

      <DialogContent>
        <DialogContentTitle id="transfer-device-description">
          {t('pages.devices.dialogs.transfer.description')}
        </DialogContentTitle>
        <FormControl fullWidth variant="outlined">
          <Select
            displayEmpty
            fullWidth
            inputProps={{
              'aria-label': t('pages.devices.dialogs.transfer.network_label'),
              'aria-describedby': 'transfer-device-description',
            }}
            onChange={handleNetwork}
            value={networkId}
          >
            {currentUser.networks.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={() => setOpen(false)}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          onClick={transfer}
          variant="contained"
          disabled={currentNetwork.id === networkId}
        >
          {t('components.transfer_dialog.content.transfer_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
